"use client"
import FilterList from "@/app/components/auctor/filter/FilterList"
import TemplateSection from "@/app/components/auctor/tool/TemplateSection"
import ToolTemplate from "@/app/components/auctor/tool/ToolTemplate"
import {IAppliedFilter, IFilterConfig} from "@/app/models/auctor"
import {evaluateExpressionWithFiltersAndRowData, getClientQueryParams} from "@/app/utils/expression-utils"
import {evaluateDynamicDefaultValue, initAppliedFilters} from "@/app/utils/filters-utils"
import dynamic from "next/dynamic"
import React, {useEffect, useMemo, useRef, useState} from "react"

const AppIcon = dynamic(() => import("@/app/components/atomic/AppIcon"))

const LIST_LOADING_DURATION = 100
const DEFAULT_VISIBLE_FILTERS = 5

interface ToolProps {
    title?: string
    template: any
    filters: IFilterConfig[]
    pageConfig?: any
    isPreview?: boolean
    expressionContext?: string
    expressionOnLoad?: string
    showFiltersAbove?: boolean
    maxFiltersMobile?: number
    toolData?: any
    mobileTemplateSticky?: boolean
    isEmbedded?: boolean
}

export default function Tool({
                                 title,
                                 template,
                                 filters,
                                 pageConfig,
                                 expressionContext,
                                 expressionOnLoad,
                                 showFiltersAbove = false,
                                 maxFiltersMobile = DEFAULT_VISIBLE_FILTERS,
                                 mobileTemplateSticky = true,
                                 isPreview,
                                 toolData,
                                 isEmbedded = false,
                             }: ToolProps) {
    // Get query parameters for filter initialization
    const queryParams = getClientQueryParams()

    // Use useMemo with proper dependencies to prevent unnecessary recalculations
    // This ensures useState only gets a stable initial value
    const initialAppliedFilters = useMemo(
        () => initAppliedFilters(filters, pageConfig, queryParams),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, pageConfig]
    )

    const [appliedFilters, setAppliedFilters] = useState<IAppliedFilter[]>(initialAppliedFilters)
    // loadContext defaults to an empty object so expressions can read its properties before expression_on_load runs
    const [loadContextCode, setLoadContextCode] = useState<string>("const loadContext = {};")
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const expressionOnLoadContext = useMemo(
        () => (expressionContext ? `${loadContextCode} ${expressionContext}` : loadContextCode),
        [loadContextCode, expressionContext]
    )

    const [isFilterListExpandedOnMobile, setIsFilterListExpandedOnMobile] = useState<boolean>(false)
    const [maxFilterToDisplay, setMaxFilterToDisplay] = useState<number>(
        Math.min(maxFiltersMobile, filters?.length ?? 0)
    )

    const templateRef = useRef<HTMLDivElement | null>(null)
    const initialAppliedFiltersRef = useRef<IAppliedFilter[]>(initialAppliedFilters)
    const hasEvaluatedExpressionOnLoad = useRef<boolean>(false)

    const updateAndApplyDynamicValues = (filterToUpdate: IAppliedFilter, newValue: any) => {
        setAppliedFilters((prevAppliedFilters) => {
            // First, update the applied filter value
            let updatedFilters = prevAppliedFilters.map((aFilter) => {
                if (aFilter.filterId === filterToUpdate.filterId) {
                    return {...aFilter, value: newValue}
                }
                return aFilter
            })

            // Re-evaluate dynamic values for other filters that depend on the changed filter
            updatedFilters = updatedFilters.map((aFilter) => {
                if (aFilter.dynamicValueExpression && aFilter.filterId !== filterToUpdate.filterId) {
                    const filterConfig = filters.find((f: IFilterConfig) => f.unique_id === aFilter.filterId)
                    if (filterConfig) {
                        const dynamicValue = evaluateDynamicDefaultValue(filterConfig, updatedFilters, queryParams)
                        if (dynamicValue !== null && dynamicValue !== undefined) {
                            return {...aFilter, value: dynamicValue}
                        }
                    }
                }
                return aFilter
            })

            return updatedFilters
        })

        setIsLoading(true)
        setTimeout(() => setIsLoading(false), LIST_LOADING_DURATION)
    }

    const hasFilters = filters?.length > 0

    useEffect(() => {
        setIsLoading(false)

        // Ensure window is available before evaluating expressions that might use it
        if (typeof window === "undefined") {
            return
        }

        // expression_on_load only runs on the tool's initial load, otherwise it would overwrite user-edited filters
        if (!expressionOnLoad || hasEvaluatedExpressionOnLoad.current) {
            return
        }
        hasEvaluatedExpressionOnLoad.current = true

        // expression_on_load can prefill filters: evaluate it on a copy, then commit via setAppliedFilters
        const filtersSnapshot = initialAppliedFiltersRef.current.map((aFilter) => ({...aFilter}))
        const loadContextValue = evaluateExpressionWithFiltersAndRowData(expressionOnLoad, toolData, filtersSnapshot)
        const safeLoadContext = loadContextValue && typeof loadContextValue === "object" ? loadContextValue : {}

        setLoadContextCode(`const loadContext = ${JSON.stringify(safeLoadContext)};`)
        setAppliedFilters(filtersSnapshot)
    }, [expressionOnLoad, toolData])

    return (
        <>
            <div id="tool-section" className="relative w-full bg-neutral flex flex-col items-center justify-center">
                {/* Mobile top template - show on mobile */}
                <div className="lg:hidden w-full">
                    <MobileTopTemplate
                        templateRef={templateRef}
                        template={template}
                        toolData={toolData}
                        appliedFilters={appliedFilters}
                        expressionContext={expressionOnLoadContext}
                        mobileTemplateSticky={mobileTemplateSticky}
                    />
                </div>

                <div className="w-full p-6 h-full bg-white max-w-[960px] my-2 lg:my-4 rounded-2xl shadow-xl z-10">
                    {title && <h2 className="px-0 mb-6 text-[22px] leading-tight">{title}</h2>}
                    <div
                        className="flex justify-center relative flex-col gap-8 lg:flex-col place-self-center mx-auto w-full"
                    >
                        {hasFilters && (
                            <div
                                className="grid items-center content-center h-fit w-full z-10"
                            >
                                <FilterList
                                    className="gap-6 grid grid-cols-1 md:grid-cols-2 md:items-start"
                                    variant="tool"
                                    filtersConfig={filters}
                                    appliedFilters={appliedFilters}
                                    updateAppliedFilter={updateAndApplyDynamicValues}
                                    displayNumberOnMobile={maxFilterToDisplay}
                                    isFilterListExpandedOnMobile={isFilterListExpandedOnMobile}
                                    queryParams={queryParams}
                                />

                                {filters?.length > maxFilterToDisplay && (
                                    <div
                                        className="flex md:hidden bg-rm-blue text-white p-2 rounded-b-md cursor-pointer justify-center items-center"
                                        onClick={() =>
                                            setIsFilterListExpandedOnMobile(!isFilterListExpandedOnMobile)
                                        }
                                    >
                                        <AppIcon
                                            type="regular"
                                            iconClassName={
                                                isFilterListExpandedOnMobile ? "fa-circle-minus" : "fa-circle-plus"
                                            }
                                            className="mr-2"
                                        />
                                        Afficher {isFilterListExpandedOnMobile ? "moins" : "plus"} de critères
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Desktop template */}
                        <div className="hidden lg:flex flex-col h-full w-full">
                            <ToolTemplate
                                sections={template.sections}
                                appliedFilters={appliedFilters}
                                toolData={toolData}
                                expressionContext={expressionOnLoadContext}
                            />
                        </div>

                        {/* Mobile template */}
                        <div className="flex lg:hidden flex-col h-full w-full">
                            <ToolTemplate
                                sections={template.sections.slice(1)}
                                appliedFilters={appliedFilters}
                                toolData={toolData}
                                expressionContext={expressionOnLoadContext}
                                afterFilterSectionsOnly
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

interface MobileTopTemplateProps {
    templateRef: React.RefObject<HTMLDivElement | null>
    template: any
    appliedFilters: IAppliedFilter[]
    expressionContext?: string
    toolData?: any
    mobileTemplateSticky: boolean
}

function MobileTopTemplate({
                               templateRef,
                               template,
                               appliedFilters,
                               expressionContext,
                               toolData,
                               mobileTemplateSticky,
                           }: MobileTopTemplateProps) {
    const scrollToTemplate = () => {
        if (templateRef.current) {
            window.scrollTo({
                top: templateRef.current.offsetTop,
                behavior: "smooth",
            })
        }
    }

    return (
        <>
            <div
                onClick={scrollToTemplate}
                className={`top-2 mt-2 px-2 z-40 w-full flex justify-center lg:hidden
                           ${mobileTemplateSticky ? "sticky" : ""}`}
            >
                <TemplateSection
                    section={template.sections[0]}
                    appliedFilters={appliedFilters}
                    expressionContext={expressionContext}
                    toolData={toolData}
                    key={`section-floating-${template.sections[0].id}`}
                    isFloatingTemplate
                />
            </div>

            <div ref={templateRef} className={`flex flex-col h-full w-full mt-2 px-2 bg-blue-100 lg:hidden`}>
                <ToolTemplate
                    sections={template.sections.slice(1)}
                    toolData={toolData}
                    appliedFilters={appliedFilters}
                    expressionContext={expressionContext}
                    beforeFilterSectionsOnly
                />
            </div>
        </>
    )
}
